/* eslint-disable no-underscore-dangle */
import { BankFilled, MailFilled, UserOutlined } from '@ant-design/icons';
import {
  CardAvatar,
  CardHeader,
  VerticalSpace,
} from '@brokerbay/design-components';
import { Country } from '@bb/domain/dist/types';
import { MemberNumberState } from '@bb/users';
import { MailerController, SecretsController } from 'api';
import { message } from 'components';
import React from 'react';
import { SALES_EMAIL, SUPPORT_EMAIL } from 'utils/constants';
import type {
  IContactSalesFormBody,
  IGetOrgMemberNumberStateResponse,
} from '@/app-package';
import errorReporter from '../../../../app/errorReporter';
import {
  OnboardingAgentVerificationWrapper,
  OnboardingContactSalesForm,
  OnboardingMemberIdForm,
  OnboardingMemberIdFormProps,
  OnboardingMemberIdFormValues,
} from '../../components';
import { AgentVerificationPageState } from '../../types';
import EmailSentView from './EmailSentView';

const TITLE_MAP: Partial<Record<AgentVerificationPageState, string>> = {
  contactOffice: 'You already have an account',
  contactSales: 'Contact our sales team',
  wrongOrg: 'Can’t locate your Member ID',
};

const ICON_MAP: Partial<
  Record<AgentVerificationPageState, React.ComponentType>
> = { contactOffice: UserOutlined, contactSales: MailFilled };

type claimProfileCallType = (
  values: Omit<OnboardingMemberIdFormValues, 'country'>,
) => Promise<{
  result: IGetOrgMemberNumberStateResponse;
  pageState?: AgentVerificationPageState;
  error?: string;
}>;

export interface AgentVerificationProps {
  claimProfileCall: claimProfileCallType;
  resendVerificationCall: claimProfileCallType;
  onRequestStepBack?: () => void;
}

export const AgentVerificationView = ({
  claimProfileCall,
  resendVerificationCall,
  onRequestStepBack,
}: AgentVerificationProps) => {
  const [pageState, setPageState] =
    React.useState<AgentVerificationPageState>('default');
  const [memberNumberState, setMemberNumberState] = React.useState<
    MemberNumberState | undefined
  >(undefined);
  const [boardId, setBoardId] = React.useState<string | undefined>();
  const [memberId, setMemberId] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [working, setWorking] = React.useState(false);
  const [error, setError] = React.useState<string | React.ReactNode>('');
  const [authUrl, setAuthUrl] = React.useState('');
  const [orgName, setOrgName] = React.useState<string | undefined>(undefined);

  React.useEffect(() => {
    const fetchAuthUrl = async () => {
      try {
        const data = await SecretsController.getAuthUrl();
        setAuthUrl(data.authUrl);
      } catch (err) {
        errorReporter.error(err);
        setAuthUrl('https://auth.brokerbay.com'); // Set default
      }
    };

    fetchAuthUrl();
  }, []);

  const onSubmit = async (values: OnboardingMemberIdFormValues) => {
    setWorking(true);
    setBoardId(values.boardId);
    setMemberId(values.memberId);

    try {
      const {
        result,
        pageState: newPageState,
        error: newError,
      } = await claimProfileCall(values);
      setMemberNumberState(result.state);
      setEmail(result.email);
      setOrgName(result.orgName);
      setWorking(false);
      setError(newError || '');
      setPageState(newPageState || pageState);
    } catch (err) {
      setError(
        <>
          It looks like we’re having trouble identifying your member ID. Please
          try again or contact{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> for
          assistance.
        </>,
      );
      errorReporter.error(err);
      setWorking(false);
    }
  };

  const goBack = () => {
    if (pageState === 'default') {
      window.history.back(); // Go back to auth server
    } else {
      setPageState('default');
      setMemberNumberState(undefined);
    }
  };

  const resendVerification = async () => {
    if (working) return;
    setWorking(true);

    try {
      const { pageState: newPageState, error: newError } =
        await resendVerificationCall({ boardId, memberId });

      setError(newError || '');
      setPageState(newPageState || pageState);
      setWorking(false);
    } catch (err) {
      setError(
        <>
          Could not resend verification. Please try again or contact{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> for
          assistance.
        </>,
      );
      setWorking(false);
    }
  };

  const contactSales = async (values: IContactSalesFormBody) => {
    if (working) return;
    setWorking(true);

    try {
      await MailerController.contactSalesForm(values);

      setWorking(false);
      setPageState('salesFormSubmitted');
    } catch (err) {
      message.error(
        <>
          Could not submit form. Please try again or contact us directly at{' '}
          <a href={`mailto:${SALES_EMAIL}`}>{SALES_EMAIL}</a>
        </>,
      );
      setWorking(false);
    }
  };

  const _buildLoginUrl = () => {
    const [subdomain] = window.location.hostname.split('.');
    const redirect = `${authUrl}?client=${subdomain}`;

    return redirect;
  };

  const loginUrl = _buildLoginUrl();

  const redirectToLogin = () => {
    window.location.href = loginUrl;
  };

  const title = React.useMemo(() => {
    if (pageState === 'verificationSent') return 'Check your email';
    return TITLE_MAP[pageState] || 'What is your member ID?';
  }, [pageState]);
  const icon = React.useMemo(() => {
    if (pageState === 'verificationSent') return MailFilled;
    return ICON_MAP[pageState] || BankFilled;
  }, [pageState]);

  const _buildContent = () => {
    let body = null;

    if (pageState === 'default' || pageState === 'wrongOrg') {
      const isWrongOrg = pageState === 'wrongOrg';
      body = (
        <OnboardingMemberIdForm
          onSubmit={onSubmit}
          inputDisabled={isWrongOrg}
          hideSubmit={isWrongOrg}
          working={working}
        />
      );
    } else if (pageState === 'salesFormSubmitted') {
      body = <EmailSentView loginUrl={loginUrl} />;
    } else if (pageState === 'contactSales') {
      body = (
        <OnboardingContactSalesForm
          reason={memberNumberState}
          onSubmit={contactSales}
          working={working}
        />
      );
    } else if (['verificationSent', 'contactOffice'].includes(pageState)) {
      const formProps: Partial<OnboardingMemberIdFormProps> = {
        initialValues: { boardId, memberId, country: Country.USA },
        inputDisabled: true,
        working,
      };

      if (pageState === 'verificationSent') {
        formProps.onSubmit = resendVerification;
        formProps.submitLabel = 'Resend Verification';
      }

      if (pageState === 'contactOffice') {
        formProps.onSubmit = redirectToLogin;
        formProps.submitLabel = 'Back to Login';
      }

      body = <OnboardingMemberIdForm {...formProps} />;
    } else {
      throw new Error('Invalid page state');
    }

    return body;
  };

  return (
    <VerticalSpace>
      {pageState !== 'salesFormSubmitted' && (
        <CardHeader
          title={title}
          icon={<CardAvatar icon={icon} />}
          onRequestStepBack={onRequestStepBack || goBack}
        />
      )}

      <OnboardingAgentVerificationWrapper
        pageState={pageState}
        memberNumberState={memberNumberState}
        email={email}
        error={error}
        orgName={orgName}
        authUrl={authUrl}
      >
        {_buildContent()}
      </OnboardingAgentVerificationWrapper>
    </VerticalSpace>
  );
};
